import { MedicalChronologyConstants } from "@/components/MedicalChronology/constants";
import { LabeledSwitch } from "@/components/ui/LabeledSwitch";
import { formatDate } from "@/utils";
import { cn } from "@paribus/shared-ui";
import { Save, Search, X } from "lucide-react";
import React, { useMemo } from "react";

import { haystackMatchesQuery } from "./lib/summarySearchUtils";
import {
  hasAnyClinicalHighlightsDisplayContent,
  hasAnyPatientBackgroundDisplayContent,
} from "./lib/summaryTabTextDisplay";
import type { SummaryTabViewProps } from "./types";
import {
  SummaryBackgroundSection,
  SummaryClinicalHighlightsSection,
  SummaryPatientSection,
} from "./ui";

export type { SummaryTabViewProps } from "./types";

export const SummaryTabView: React.FC<SummaryTabViewProps> = ({
  isLoading,
  isEditMode,
  isEditModeAvailable,
  isSaving,
  hasDraftChanges,
  summaryData,
  summaryDraft,
  backgroundDraft,
  setSummaryDraft,
  setBackgroundDraft,
  onToggleEditMode,
  onSave,
  onCancel,
  searchQuery,
  setSearchQuery,
  debouncedSearchQuery,
  searchInputRef,
}) => {
  const isSearching = Boolean(debouncedSearchQuery.trim());

  const { showPatient, showBackground, showHighlights } = useMemo(() => {
    const q = debouncedSearchQuery;
    const dobFormatted = formatDate(summaryDraft.date_of_birth);
    const doiFormatted = formatDate(summaryDraft.date_of_injury);
    const showPatient = haystackMatchesQuery(
      q,
      summaryDraft.patient_name,
      summaryDraft.date_of_birth,
      summaryDraft.date_of_injury,
      dobFormatted,
      doiFormatted,
      summaryDraft.accident_description,
      "Patient Information",
      "Demographics",
      "incident",
      "Date of Birth",
      "Date of Injury"
    );
    const showBackground =
      (isEditMode || hasAnyPatientBackgroundDisplayContent(backgroundDraft)) &&
      haystackMatchesQuery(
        q,
        backgroundDraft.prior_medical_history,
        backgroundDraft.pre_existing_conditions,
        backgroundDraft.family_history,
        backgroundDraft.social_history,
        backgroundDraft.allergies_medications,
        "Patient Background",
        "Prior Medical",
        "Pre-existing",
        "Family History",
        "Social History",
        "Allergies",
        "Medications"
      );
    const showHighlights =
      (isEditMode || hasAnyClinicalHighlightsDisplayContent(summaryDraft)) &&
      haystackMatchesQuery(
        q,
        summaryDraft.key_injuries,
        summaryDraft.key_test_results,
        summaryDraft.key_diagnoses,
        "Case highlights",
        "Key Injuries",
        "Key Test",
        "Key Diagnoses"
      );
    return { showPatient, showBackground, showHighlights };
  }, [debouncedSearchQuery, summaryDraft, backgroundDraft, isEditMode]);

  const saveDisabled = isSaving || !hasDraftChanges;

  return (
    <div className={cn("space-y-4 pb-6", isEditMode && "pb-24")}>
      <div className="sticky top-0 z-20 bg-surface-subtle pb-2 shadow-[0_1px_0_0_rgba(0,0,0,0.04)]">
        <div className="flex min-w-0 items-center gap-3 px-1 py-1">
          <div className="flex h-10 min-w-0 max-w-sm flex-1 items-center gap-2 rounded-xl border border-neutral-200/80 bg-neutral-50/50 px-3 py-1 transition-colors focus-within:shadow-focus-inset hover:bg-neutral-100/50">
            <Search size={14} strokeWidth={2.5} className="shrink-0 text-neutral-400" aria-hidden />
            <input
              ref={searchInputRef}
              type="text"
              aria-label="Search summary"
              placeholder="Search summary…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="min-w-0 flex-1 bg-transparent text-xs font-medium text-neutral-700 placeholder:text-neutral-400 focus:outline-none"
            />
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchQuery("")}
              className={`shrink-0 cursor-pointer text-neutral-400 transition-opacity hover:text-neutral-600 ${searchQuery ? "opacity-100" : "pointer-events-none opacity-0"}`}
            >
              <X size={12} strokeWidth={2.5} />
            </button>
          </div>
          <div className="ml-auto shrink-0">
            <LabeledSwitch
              aria-label="Edit mode"
              checked={isEditMode}
              onCheckedChange={onToggleEditMode}
              label="Edit Mode"
              variant="red"
              disabled={!isEditModeAvailable}
              disabledReason={MedicalChronologyConstants.FINISHED_CASE_READ_ONLY_REASON}
            />
          </div>
        </div>
      </div>

      {isLoading && !summaryData ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse rounded-xl border border-neutral-100 bg-card p-6">
              <div className="mb-4 h-3 w-1/4 rounded bg-neutral-100" />
              <div className="space-y-2">
                <div className="h-2.5 w-3/4 rounded bg-neutral-100" />
                <div className="h-2.5 w-1/2 rounded bg-neutral-100" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-5">
          {showPatient && (
            <SummaryPatientSection
              isEditMode={isEditMode}
              summaryDraft={summaryDraft}
              setSummaryDraft={setSummaryDraft}
              highlightQuery={debouncedSearchQuery}
            />
          )}
          {showBackground && (
            <SummaryBackgroundSection
              isEditMode={isEditMode}
              backgroundDraft={backgroundDraft}
              setBackgroundDraft={setBackgroundDraft}
              highlightQuery={debouncedSearchQuery}
            />
          )}
          {showHighlights && (
            <SummaryClinicalHighlightsSection
              isEditMode={isEditMode}
              isSearching={isSearching}
              summaryDraft={summaryDraft}
              setSummaryDraft={setSummaryDraft}
              highlightQuery={debouncedSearchQuery}
            />
          )}
        </div>
      )}

      {isEditMode && (
        <div className="fixed bottom-0 left-0 right-0 z-fixed flex items-center justify-center p-4 sm:p-6">
          <div className="flex w-full max-w-2xl items-center justify-between gap-4 rounded-xl border border-neutral-200 bg-card/80 p-4 shadow-xl backdrop-blur-md">
            <div className="flex flex-col">
              <span className="text-xs font-bold text-neutral-900">
                {hasDraftChanges ? "Unsaved changes" : "No changes yet"}
              </span>
              <span className="text-2xs font-medium text-neutral-500">
                {hasDraftChanges
                  ? "Save or cancel when you are done"
                  : "Edit a field above to enable Save"}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={isSaving}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-neutral-200 bg-card px-5 text-sm font-bold text-neutral-600 transition-all hover:bg-neutral-50 hover:text-neutral-900 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={16} strokeWidth={2.5} />
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saveDisabled}
                className={cn(
                  "inline-flex h-10 items-center gap-2 rounded-xl px-6 text-sm font-bold transition-all active:scale-[0.98] disabled:cursor-not-allowed",
                  isSaving && "bg-primary text-white shadow-sm hover:bg-primary-hover",
                  !isSaving &&
                    hasDraftChanges &&
                    "bg-primary text-white shadow-sm hover:bg-primary-hover hover:shadow-md",
                  !isSaving &&
                    !hasDraftChanges &&
                    "bg-neutral-200 text-neutral-400 shadow-none hover:bg-neutral-200"
                )}
              >
                {isSaving ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                ) : (
                  <Save size={16} strokeWidth={2.5} />
                )}
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
